import { html } from "../utils/dom-utils";
import { Option } from "../utils/validation";
import { DataFetch } from "./base-component";
import { OptionFieldValidator } from "../utils/option-field-validator";
import { OptionFieldManager } from "../utils/option-field-manager";

export function setupMultiselect(
  input: HTMLInputElement,
  shownInput: HTMLInputElement,
  debounce: number | null,
  dataFetch: DataFetch,
  displayProperty: string,
  valueProperty: string,
  initialAvailableOptions: Option[] = [],
) {
  const dropdownButton = document.getElementById(`${input.id}-dropdown-btn`);
  if (dropdownButton == null) {
    console.error("Dropdown button is missing");
    return;
  }

  // Track selected values and available options
  let selectedOptions: Option[] = [];
  let availableOptions: Option[] = initialAvailableOptions;

  // Set up validation using new unified system
  const fieldGroup = input.closest("[data-option-field-group]") as HTMLElement;
  if (fieldGroup) {
    const manager = new OptionFieldManager(fieldGroup);
    const validator = new OptionFieldValidator(
      () => availableOptions, // Use all available options for validation
      () => input.value, // Get hidden input value (input is the hidden input parameter)
    );
    const fieldName = input.getAttribute("data-field-name") || "Field";
    manager.setupValidation(validator, [], fieldName);
  }

  // Initialize from existing value
  if (input.value) {
    const values = input.value.split(",").filter((v) => v.trim());
    // We'll need to fetch the display values for these
    if (values.length > 0) {
      setInitialValues(values);
    }
  }

  const badgeContainer = createBadgeContainer();

  const suggestionsId = `${input.id}-suggestions`;
  const newSuggestionBuilder = () => {
    const suggestions = html`
    <div class="dxbl-dropdown" id="${suggestionsId}-box" style="position: absolute; z-index: 1000; display: none;"
         role="listbox" aria-label="Suggestions">
      <div class="dxbl-dropdown-dialog dxbl-edit-dropdown dxbl-edit-dropdown">
        <div class="dxbl-dropdown-body dxbl-edit-dropdown-body">
          <div class="dxbl-listbox">
            <ul id=${suggestionsId} class="dxbs-dm" />
          </div>
        </div>
      </div>
    </div>
    `;
    if (suggestions instanceof Array) throw Error("Should only get one el");
    document.body.appendChild(suggestions);
    return document.getElementById(suggestionsId)!;
  };
  const suggestions =
    document.getElementById(suggestionsId) ?? newSuggestionBuilder();
  const suggestionBox = document.getElementById(suggestionsId + "-box")!;

  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", suggestionsId);
  input.setAttribute("aria-expanded", "false");

  let currentSuggestionIndex = -1;
  let suggestionItems: HTMLElement[] = [];
  let debounceTimer: number | null = null;

  function createBadgeContainer(): HTMLElement {
    const badgeId = `${input.id}-badges`;
    const existing = document.getElementById(badgeId);
    if (existing) return existing;

    const container = html`<div id="${badgeId}" class="multiselect-badges" style="margin-top: 5px;"></div>`;
    if (container instanceof Array) throw Error("Should only get one el");

    const inputContainer = document.getElementById(`${input.id}-container`)!;
    const inputDiv = inputContainer.querySelector(".dxbl-text-edit")!;
    inputContainer.insertBefore(container, inputDiv.nextSibling);
    return container;
  }

  function updateBadges() {
    badgeContainer.innerHTML = "";
    selectedOptions.forEach((option, index) => {
      const badge = html`
        <span class="badge badge-primary" style="margin-right: 5px; margin-bottom: 5px; display: inline-flex; align-items: center;">
          ${option.display}
          <button type="button" class="badge-remove" style="margin-left: 5px; background: none; border: none; color: white; cursor: pointer; font-size: 16px;" data-remove-index="${index}">×</button>
        </span>
      `;
      if (badge instanceof Array) throw Error("Should only get one el");
      badgeContainer.appendChild(badge);
    });

    badgeContainer.querySelectorAll(".badge-remove").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const index = parseInt(
          (e.target as HTMLElement).getAttribute("data-remove-index")!,
        );
        removeSelectedOption(index);
      });
    });
  }

  function addSelectedOption(option: Option) {
    if (selectedOptions.some((selected) => selected.value === option.value)) {
      return;
    }

    // Handle "All Counties" mutual exclusivity
    if (option.value === "35000") {
      // If "All Counties" is selected, clear all other selections
      selectedOptions = [option];
    } else if (selectedOptions.some((o) => o.value === "35000")) {
      // If any specific county is selected, remove "All Counties"
      selectedOptions = selectedOptions.filter((o) => o.value !== "35000");
      selectedOptions.push(option);
    } else {
      // Normal multi-select behavior
      selectedOptions.push(option);
    }

    updateHiddenInput();
    updateBadges();

    shownInput.value = "";

    const changeEvent = new Event("change", { bubbles: true });
    input.dispatchEvent(changeEvent);
  }

  function removeSelectedOption(index: number) {
    selectedOptions.splice(index, 1);
    updateHiddenInput();
    updateBadges();

    shownInput.value = "";

    const changeEvent = new Event("change", { bubbles: true });
    input.dispatchEvent(changeEvent);
  }

  function updateHiddenInput() {
    input.value = selectedOptions.map((opt) => opt.value).join(",");
  }

  async function setInitialValues(values: string[]) {
    try {
      input.classList.add("loading");
      const results = await dataFetch.fetchSuggestions(
        "",
        displayProperty,
        valueProperty,
      );

      if (results) {
        availableOptions = results;

        selectedOptions = values.map((value) => {
          const option = results.find(
            (r) =>
              r.value.toString().toLowerCase().trim() ===
              value.toString().toLowerCase().trim(),
          );
          return option || { value, display: value }; // fallback to value as display
        });
        updateBadges();

        // Trigger validation update through input event with proper timing
        setTimeout(() => {
          shownInput.dispatchEvent(new Event("input", { bubbles: true }));
        }, 0);

        // Trigger change event to notify validation system
        const event = new Event("change", { bubbles: true });
        input.dispatchEvent(event);
      }
    } catch (error) {
      console.error("Error setting initial multiselect values:", error);
    } finally {
      input.classList.remove("loading");
    }
  }

  // Clean up any existing event listeners when re-initializing
  const cleanupEventListeners = () => {
    document.removeEventListener("click", handleOutsideClick);
  };

  // Add input event listener with debounce
  const handleInput = () => {
    if (debounceTimer) {
      window.clearTimeout(debounceTimer);
    }
    debounceTimer = window.setTimeout(() => {
      fetchAndShowSuggestions();
    }, debounce ?? 0);
  };

  shownInput.addEventListener("input", (e) => {
    if (e.target != document.activeElement) return;

    if (shownInput.value.trim() === "") {
      // Trigger change event so form validation can update
      const event = new Event("change", { bubbles: true });
      input.dispatchEvent(event);
    }

    handleInput();
  });

  dropdownButton.addEventListener("click", (e) => {
    e.stopPropagation();
    if (suggestionBox.style.display === "block") {
      hideSuggestions();
    } else {
      fetchAndShowSuggestions();
    }
  });

  shownInput.addEventListener("focus", () => {
    fetchAndShowSuggestions();
    shownInput.select();
  });

  // Keyboard navigation
  shownInput.addEventListener("keydown", function (e) {
    if (suggestionBox.style.display !== "block") return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        navigateSuggestions(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        navigateSuggestions(-1);
        break;
      case "Enter":
        e.preventDefault();
        if (
          currentSuggestionIndex >= 0 &&
          suggestionItems[currentSuggestionIndex]
        ) {
          selectSuggestion(suggestionItems[currentSuggestionIndex]);
        } else if (
          // if there is only one unambiguous match, just select it
          suggestionItems &&
          suggestionItems.length == 1
        ) {
          selectSuggestion(suggestionItems[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        hideSuggestions();
        shownInput.focus();
        break;
      case "Tab":
        hideSuggestions();
        break;
    }
  });

  async function fetchAndShowSuggestions() {
    try {
      input.classList.add("loading");
      const results = await dataFetch.fetchSuggestions(
        shownInput.value.trim(),
        displayProperty,
        valueProperty,
      );

      if (results) {
        availableOptions = results;
      }

      // Filter out already selected options
      const filteredResults = (results ?? []).filter(
        (result) =>
          !selectedOptions.some((selected) => selected.value === result.value),
      );
      showSuggestions(filteredResults);
    } catch (error) {
      console.error("Error fetching multiselect suggestions:", error);
      hideSuggestions();
    } finally {
      input.classList.remove("loading");
    }
  }

  suggestions.addEventListener("click", function (e) {
    if (
      e.target &&
      e.target instanceof HTMLElement &&
      e.target.matches("div.dxbl-listbox-item")
    ) {
      selectSuggestion(e.target);
    }
  });

  function selectSuggestion(element: HTMLElement) {
    const value = element.getAttribute("data-sync-option-value");
    if (!value) return;
    const display = element.innerText;

    addSelectedOption({ value, display });
    hideSuggestions();

    // Ensure focus returns to the visible input after keyboard selection
    shownInput.focus();
  }

  const handleOutsideClick = function (e: MouseEvent) {
    const target = e.target as Node;
    if (
      target !== shownInput &&
      target !== dropdownButton &&
      !suggestionBox.contains(target) &&
      !badgeContainer.contains(target)
    ) {
      hideSuggestions();
    }
  };

  document.addEventListener("click", handleOutsideClick);

  function navigateSuggestions(direction: number) {
    if (suggestionItems.length === 0) return;

    if (
      currentSuggestionIndex >= 0 &&
      suggestionItems[currentSuggestionIndex]
    ) {
      suggestionItems[currentSuggestionIndex].classList.remove(
        "dxbl-listbox-item-selected",
      );
      suggestionItems[currentSuggestionIndex].setAttribute(
        "aria-selected",
        "false",
      );
    }

    currentSuggestionIndex += direction;

    if (currentSuggestionIndex < 0) {
      currentSuggestionIndex = suggestionItems.length - 1;
    } else if (currentSuggestionIndex >= suggestionItems.length) {
      currentSuggestionIndex = 0;
    }

    const selectedItem = suggestionItems[currentSuggestionIndex];
    selectedItem.classList.add("dxbl-listbox-item-selected");
    selectedItem.setAttribute("aria-selected", "true");

    input.setAttribute("aria-activedescendant", selectedItem.id);

    if (selectedItem.scrollIntoView) {
      selectedItem.scrollIntoView({ block: "nearest" });
    }
  }

  function showSuggestions(items: Option[]) {
    suggestions.innerHTML = "";
    currentSuggestionIndex = -1;
    suggestionItems = [];

    if (items.length > 0) {
      items.forEach((item, index) => {
        const itemId = `${suggestionsId}-item-${index}`;
        const listItem = html`
        <li>
          <div id="${itemId}" class="dxbl-listbox-item" role="option" aria-selected="false" data-sync-option-value="${item.value}">
            ${item.display}
          </div>
        </li>
        `;
        if (listItem instanceof Array) throw Error("Should only get one ul");
        suggestions.appendChild(listItem);

        suggestionItems.push(
          listItem.querySelector("div.dxbl-listbox-item") as HTMLElement,
        );
      });

      positionSuggestionBox();

      input.setAttribute("aria-expanded", "true");
    } else {
      const noResults = html`<li><div class="dxbl-listbox-item dxbl-listbox-item-disabled">No results found</div></li>`;
      suggestions.appendChild(noResults as HTMLElement);
      positionSuggestionBox();
    }
  }

  function positionSuggestionBox() {
    const inputContainer = document.getElementById(`${input.id}-container`)!;
    const textEditDiv = inputContainer.querySelector(".dxbl-text-edit")!;
    const rect = textEditDiv.getBoundingClientRect();

    suggestionBox.style.position = "absolute";
    suggestionBox.style.top = `${rect.bottom + window.scrollY}px`;
    suggestionBox.style.left = `${rect.left + window.scrollX}px`;
    suggestionBox.style.width = `${rect.width}px`;
    suggestionBox.style.zIndex = "1000";
    suggestionBox.style.transform = "none";
    suggestionBox.style.display = "block";
  }

  function hideSuggestions() {
    suggestionBox.style.display = "none";
    currentSuggestionIndex = -1;
    suggestionItems = [];
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        for (const node of Array.from(mutation.removedNodes)) {
          if (node.contains && (node.contains(input) || node === input)) {
            cleanupEventListeners();
            observer.disconnect();
            return;
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

export class AsyncMultiselectDataFetch implements DataFetch {
  constructor(
    private apiEndpoint: string,
    private callback: (options: Option[]) => void,
  ) {}

  private onFetch: ((opt: Promise<Option[]>) => void) | null = null;

  private previousFetches: Promise<Option[]>[] = [];

  public async fetchSuggestions(
    value: string,
    displayProperty: string,
    valueProperty: string,
  ): Promise<Option[]> {
    for (const previous of this.previousFetches) {
      Promise.all([previous, Promise.reject()]);
    }
    const initialRequest = fetch(
      `${this.apiEndpoint}/${encodeURIComponent(value)}`,
    );
    const request: Promise<Option[]> = initialRequest.then((response) => {
      if (!response.ok)
        throw new Error(`Network response was not ok: ${response.status}`);

      return (response.json() as Promise<any[]>).then((opts) => {
        const options: Option[] = opts.map((o) => ({
          value: String(o[valueProperty] ?? ""),
          display: String(o[displayProperty] ?? ""),
        }));
        this.callback(options);
        return options;
      });
    });
    this.previousFetches.push(request);
    if (this.onFetch != null) {
      this.onFetch(request);
    }
    return request;
  }

  public registerOnFetch(onFetch: (opts: Promise<Option[]>) => void): void {
    this.onFetch = onFetch;
  }
}

export class SyncMultiselectDataFetch implements DataFetch {
  constructor(private options: Option[]) {}

  public async fetchSuggestions(value: string): Promise<Option[]> {
    const optionPromise = Promise.resolve(
      this.options.filter((o) =>
        o.display.toLowerCase().startsWith(value.toLowerCase()),
      ),
    );
    if (this.onFetch) this.onFetch(optionPromise);
    return optionPromise;
  }
  private onFetch: ((opt: Promise<Option[]>) => void) | null = null;

  public registerOnFetch(onFetch: (opts: Promise<Option[]>) => void): void {
    this.onFetch = onFetch;
  }
}
export class GenericMultiselect {
  constructor(private debounce: number | null) {}

  // For Synchronous multiselect validation
  private formOptionMap: { [key: string]: Option[] } = {};

  // For Asynchronous multiselect validation
  private dataFetchMap: {
    [key: string]: DataFetch;
  } = {};

  public init() {
    document
      .querySelectorAll("input[data-async-multiselect]")
      .forEach((input) => {
        const shownInput =
          input.parentElement?.getElementsByTagName("input")[0]!;

        const apiEndpoint = input.getAttribute("data-api-endpoint");
        const displayProperty = input.getAttribute("data-display-property");
        const valueProperty = input.getAttribute("data-value-property");

        if (apiEndpoint == null || displayProperty == null) {
          console.error(
            "Multiselect input is missing required data attributes",
            input,
          );
          return;
        }

        const dataFetch = new AsyncMultiselectDataFetch(
          apiEndpoint,
          (options) => {
            this.formOptionMap[input.id] = options;
          },
        );
        this.dataFetchMap[input.id] = dataFetch;

        setupMultiselect(
          input as HTMLInputElement,
          shownInput,
          this.debounce,
          dataFetch,
          displayProperty!,
          valueProperty!,
          [], // Async multiselect will populate availableOptions when data is fetched
        );
      });

    document
      .querySelectorAll("input[data-sync-multiselect]")
      .forEach((input) => {
        const displayProperty = input.getAttribute("data-display-property");
        const shownInput =
          input.parentElement?.getElementsByTagName("input")[0]!;
        const valueProperty = input.getAttribute("data-value-property");
        if (displayProperty == null) {
          console.error(
            "Multiselect input is missing required data attributes",
            input,
          );
          return;
        }
        const optionContainer = input.parentElement?.querySelectorAll(
          "div[data-sync-typeahead-options]",
        );
        if (optionContainer == null || optionContainer.length != 1) {
          console.error(
            "Multiselect input is missing required data attributes",
            input,
          );
          return;
        }
        const options: Option[] = [];
        optionContainer[0]
          .querySelectorAll("div[data-sync-option-value]")
          .forEach((optElement) => {
            options.push({
              value: optElement.getAttribute("data-sync-option-value")!,
              display: optElement.innerHTML.trim(),
            });
          });
        this.formOptionMap[input.id] = options;
        optionContainer[0].classList.add("d-none");

        setupMultiselect(
          input as HTMLInputElement,
          shownInput,
          null,
          new SyncMultiselectDataFetch(options),
          displayProperty,
          valueProperty!,
          options,
        );
      });
  }

  public getFormWithOptions(name: string): Option[] {
    return this.formOptionMap[name] ?? [];
  }

  public getDataFetch(name: string): DataFetch {
    return this.dataFetchMap[name];
  }
}
